import { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard"
import { RESELLER_MANAGEMENT_TAB_IDS, ResellerManagementTabs, type ResellerManagementTabId } from "./ResellerManagementTabs"
import { ResellerApplicationsTab } from "./applications/ResellerApplicationsTab"
import { useResellerApplications } from "./applications/useResellerApplications"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import { MultiUserSelector } from "@crm-src/components/multi-user-selector"
import { BulkDeleteResellerModal } from "@crm-src/components/bulk-delete-reseller-modal"
import { BulkAssignSalespeopleModal } from "@crm-src/components/bulk-assign-salespeople-modal"
import usePermissions from "@crm-src/hooks/usePermissions"
import {
  Building2,
  Plus,
  Edit,
  Search,
  Trash2,
  MapPin,
  Eye,
  MoreHorizontal,
  Info,
  Users
} from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useResellers } from "@crm-src/hooks/useResellers"
import { Reseller } from "@crm-src/types/lead"
import { ImportExportButtons } from "@crm-src/components/import-export-buttons"
import { resellersApiService, type ResellerListFilters } from "@crm-src/lib/resellers-api"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { useTableSelection } from "@crm-src/components/table/useTableSelection"
import { buildTableParams } from "@crm-src/components/table/table-query"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { RESELLERS_TABLE, type TableColumn } from "@crm-src/components/table/configs"
import { buildResellerFacetDefs, type ResellerFacetKey } from "./resellers-page-query"
import { resellerDeleteErrorMessage } from "./reseller-delete-error"
import {
  buildResellerPayload,
  emptyResellerForm,
  resellerFormFrom,
  resellerParentOptions,
} from "./reseller-form"

/**
 * Rows per page. The list is now SERVER-paginated: it used to fetch every
 * reseller the actor could see and sort/search them in the browser, which is
 * also why not one of the seven headers could be clicked.
 */
const PER_PAGE = 25

function ResellersContent() {
  const navigate = useNavigate()
  // `useResellers()` still runs, and its list is NOT what the table renders.
  //
  // It keeps two jobs: the create / update / delete mutators, and — the reason
  // it could not simply be dropped — the FULL reseller set the Country facet
  // builds its options from. `resellers.country` is free text with a "U.S.A"
  // default, so the only honest option list is the values actually stored, and
  // deriving them from the current PAGE would make the options change under the
  // operator as they page and hide the very country they need whenever it is
  // off screen. It fetches the endpoint's BARE `{resellers, total}` shape (no
  // `page` param), which is exactly the back-compatible path this page's own
  // pagination opts out of.
  const { resellers: allResellers, createReseller, updateReseller, deleteReseller, refreshResellers } = useResellers()
  const [isResellerDialogOpen, setIsResellerDialogOpen] = useState(false)
  const [editingReseller, setEditingReseller] = useState<Reseller | null>(null)
  const { toast } = useToast()

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: TS 5.5 reads a `.filter(...)` callback as a type
  // predicate, so an inferred `facetKeys` would narrow to a different union
  // than the facet defs are built from.
  const facetKeys = useMemo<readonly ResellerFacetKey[]>(() => RESELLERS_TABLE.facetKeys, [])
  const table = useTableQuery({
    defaultSort: RESELLERS_TABLE.defaultSort,
    defaultDir: RESELLERS_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset. Search
  // text is deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(
    () => buildResellerFacetDefs(users, allResellers), [users, allResellers],
  )
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => RESELLERS_TABLE.columns, [])

  // Candidate parents for the reseller being edited: everything EXCEPT itself
  // and its own descendants.
  //
  // crm-api rejects both (self-reference and cycles are validated, because the
  // recursive subtree CTE the whole reseller wall is derived from must stay
  // acyclic). Offering a choice that can only come back 422 is worse than not
  // offering it — the operator learns the rule from a red toast instead of from
  // the list. Built from `allResellers`, the full set the hook already fetches
  // for the Country facet, so this costs no extra request.
  const parentOptions = useMemo(
    () => resellerParentOptions(allResellers, editingReseller),
    [allResellers, editingReseller],
  )

  // The rows of the CURRENT page, and what the last response reported about it.
  // The table used to render every reseller the actor could see, sorted and
  // searched in the browser.
  const [pageResellers, setPageResellers] = useState<Reseller[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false)
  const [isBulkAssignDialogOpen, setIsBulkAssignDialogOpen] = useState(false)
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  // Pending single-reseller delete (drives <ConfirmDialog>).
  const [pendingDeleteReseller, setPendingDeleteReseller] = useState<Reseller | null>(null)
  // Per-row actions menu anchor — replaces the shadcn DropdownMenu.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  // Reseller form state
  const [resellerForm, setResellerForm] = useState(emptyResellerForm())
  const [codeError, setCodeError] = useState("")
  // isAdmin() ⇔ super_admin in the CRM SPA. canManageResellers() additionally
  // covers a sales_manager (crm:resellers + crm:resellers_edit) — it gates the
  // create / edit / single-delete controls. canAssignResellerSalespeople()
  // (crm:resellers_salespeople) shows the salespeople picker in the form. Bulk
  // delete, CSV import and BULK salesperson assignment stay isAdmin()-only.
  // crm-api enforces every one of these + own-scope on its own.
  const { isAdmin, canManageResellers, canAssignResellerSalespeople, canAccessResellerApplications } =
    usePermissions()

  // ── The two tabs (2026-09-12) ────────────────────────────────────────────
  // Resellers ∣ Reseller Applications, on `?tab=` like the reseller profile.
  // The applications tab exists only for a holder of the applications grant
  // (`reseller_applications:access`); for anyone else `?tab=applications` in
  // the URL is ignored rather than opening an empty panel, and the open-queue
  // query that feeds the tab's count pill is never even sent.
  const canSeeApplications = canAccessResellerApplications()
  const [tabParam, setTab] = useTabParam(RESELLER_MANAGEMENT_TAB_IDS, "resellers")
  const activeTab: ResellerManagementTabId =
    canSeeApplications && tabParam === "applications" ? "applications" : "resellers"
  const openApplications = useResellerApplications("open", { retry: false, enabled: canSeeApplications })
  const openApplicationCount = openApplications.data?.applications.length ?? 0

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above.
  //
  // `page` is ALWAYS present, and that is load-bearing: crm-api answers the
  // paginated envelope only when it sees `page`, and the bare
  // `{resellers, total}` shape otherwise (see `resellersApiService.getAll`).
  // This page is the one caller that opts in.
  //
  // `buildTableParams` is generic over the page's facet-key union, so the
  // params type-check against `ResellerListFilters` field by field: a facet key
  // the API does not implement is a compile error here, not a param the server
  // silently drops. `table.sortExplicit` is the sixth argument — this endpoint
  // has no search-relevance branch, so it changes nothing today, but omitting
  // it defaults to `true`, which is the failure mode that silently killed
  // relevance ranking on three other pages.
  const listParams = useMemo<ResellerListFilters>(() => buildTableParams(
    { sort: table.sort, dir: table.dir },
    table.facets,
    table.page,
    PER_PAGE,
    search.search,
    table.sortExplicit,
  ), [table.sort, table.dir, table.facets, table.page, table.sortExplicit, search.search])

  // Bulk selection, CLEARED whenever the result set changes. Without that reset
  // the bulk bar stays armed against rows that are no longer on screen: tick
  // three resellers, narrow the Country facet, and "Delete Selected" would
  // delete three rows the operator cannot see. Keyed on `listParams` (compared
  // by value), so a plain refetch after a write keeps the selection.
  //
  // `T = string`: this page selects reseller IDS, not row objects.
  const selection = useTableSelection<string>(listParams)

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  const beginRequest = useLatestRequest()

  const loadPage = useCallback(async () => {
    const isCurrent = beginRequest()
    setIsLoading(true)
    try {
      const response = await resellersApiService.getPage(listParams)
      if (!isCurrent()) return
      setPageResellers(response.data)
      setTotalCount(response.meta?.pagination?.total_count ?? 0)
    } catch (error) {
      if (!isCurrent()) return
      console.error('Error loading resellers:', error)
      toast({
        title: "Error",
        description: "Failed to load resellers",
        variant: "destructive",
      })
      setPageResellers([])
      setTotalCount(0)
    } finally {
      // Guarded: a superseded request must not clear the spinner belonging to
      // the request that superseded it.
      if (isCurrent()) setIsLoading(false)
    }
  }, [listParams, beginRequest, toast])

  useEffect(() => {
    loadPage()
  }, [loadPage])

  // A write changes BOTH lists: the page being rendered and the full set the
  // Country facet's options come from.
  const refreshAll = useCallback(() => {
    loadPage()
    refreshResellers()
  }, [loadPage, refreshResellers])

  const handleViewDetails = (reseller: Reseller) => {
    navigate(`/crm/administration/resellers/${reseller.id}`)
  }

  const handleCreateReseller = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent submission if there's a code error
    if (codeError) {
      return
    }

    try {
      const resellerData = buildResellerPayload(resellerForm, {
        includeSalespeople: canAssignResellerSalespeople(),
      })

      if (editingReseller) {
        await updateReseller(editingReseller.id, resellerData)
        toast({
          title: "Success",
          description: "Reseller updated successfully"
        })
      } else {
        await createReseller(resellerData)
        toast({
          title: "Success",
          description: "Reseller created successfully"
        })
      }

      setIsResellerDialogOpen(false)
      resetResellerForm()
      // The hook's own list is not what the table renders any more, so a write
      // has to reload the PAGE too (and the full set behind the Country facet).
      refreshAll()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.errors?.join(', ') || `Failed to ${editingReseller ? 'update' : 'create'} reseller`,
        variant: "destructive"
      })
    }
  }

  const confirmDeleteReseller = async () => {
    if (!pendingDeleteReseller) return
    try {
      await deleteReseller(pendingDeleteReseller.id)
      refreshAll()
      toast({
        title: "Success",
        description: "Reseller deleted successfully"
      })
    } catch (error) {
      // The refusal SENTENCE, not a generic line: crm-api 409s with what is
      // blocking the delete (issued invoices), which is the only thing that
      // tells the operator what to do next. Until 2026-08-30 this branch was
      // unreachable — `deleteReseller` swallowed its own error — so a refused
      // delete showed the success toast above.
      toast({
        title: "Error",
        description: resellerDeleteErrorMessage(error),
        variant: "destructive"
      })
      // Keep ConfirmDialog open so the user can retry / cancel.
      throw error
    }
  }

  // Bulk selection handlers. The selection is an ARRAY of ids (see
  // `useTableSelection` above); a Set is built per render only where membership
  // is tested in a loop.
  const selectedIds = useMemo(() => new Set(selection.selected), [selection.selected])

  const handleSelectReseller = (resellerId: string, index: number, shiftKey: boolean = false) => {
    if (shiftKey && lastSelectedIndex !== null) {
      // Shift-click: select range
      const start = Math.min(lastSelectedIndex, index)
      const end = Math.max(lastSelectedIndex, index)

      const next = new Set(selection.selected)
      for (let i = start; i <= end; i++) {
        const row = pageResellers[i]
        if (row) next.add(row.id)
      }
      selection.setSelected([...next])
    } else {
      // Normal click: toggle single item
      selection.setSelected(
        selectedIds.has(resellerId)
          ? selection.selected.filter(id => id !== resellerId)
          : [...selection.selected, resellerId],
      )
    }
    setLastSelectedIndex(index)
  }

  const handleSelectAllResellers = () => {
    if (selection.selected.length === pageResellers.length) {
      selection.clear()
    } else {
      selection.setSelected(pageResellers.map(r => r.id))
    }
  }

  const handleBulkDeleteResellers = () => {
    if (selection.selected.length === 0) return
    setIsBulkDeleteDialogOpen(true)
  }

  const confirmBulkDeleteResellers = async (resellerIds: string[]) => {
    try {
      const result = await resellersApiService.bulkDelete(resellerIds)

      if (result.failed_deletions && result.failed_deletions.length > 0) {
        // Some deletions failed — say WHY for at least the first one. The
        // server reports a per-reseller reason (usually "…has issued N
        // invoices…"); a bare count leaves the operator guessing.
        const [first] = result.failed_deletions
        const reason = first?.error?.trim()
        toast({
          title: "Partial Success",
          description:
            `${result.deleted_count} deleted, ${result.failed_deletions.length} could not be. ` +
            (reason || "Open a reseller to see what is blocking it."),
          variant: "default",
        })
      } else {
        // All deletions successful
        toast({
          title: "Success",
          description: `${result.deleted_count} reseller${result.deleted_count > 1 ? 's' : ''} deleted successfully`,
        })
      }

      // Clear selection and reload resellers
      selection.clear()
      refreshAll()
    } catch (error: any) {
      console.error('Error bulk deleting resellers:', error)
      toast({
        title: "Error",
        description: resellerDeleteErrorMessage(error, "Failed to delete resellers"),
        variant: "destructive",
      })
    }
  }

  const handleBulkAssignSalespeople = () => {
    if (selection.selected.length === 0) return
    setIsBulkAssignDialogOpen(true)
  }

  const confirmBulkAssignSalespeople = async (resellerIds: string[], salespeopleIds: string[]) => {
    try {
      const result = await resellersApiService.bulkAssignSalespeople(resellerIds, salespeopleIds)

      if (result.failed_assignments && result.failed_assignments.length > 0) {
        // Some assignments failed
        toast({
          title: "Partial Success",
          description: `Salespeople assigned to ${result.assigned_resellers_count} resellers, ${result.assigned_customers_count} customers, and ${result.assigned_prospects_count} prospects. ${result.failed_assignments.length} resellers could not be updated.`,
          variant: "default",
        })
      } else {
        // All assignments successful
        const details = []
        if (result.assigned_resellers_count > 0) details.push(`${result.assigned_resellers_count} reseller${result.assigned_resellers_count > 1 ? 's' : ''}`)
        if (result.assigned_customers_count > 0) details.push(`${result.assigned_customers_count} customer${result.assigned_customers_count > 1 ? 's' : ''}`)
        if (result.assigned_prospects_count > 0) details.push(`${result.assigned_prospects_count} prospect${result.assigned_prospects_count > 1 ? 's' : ''}`)

        toast({
          title: "Success",
          description: `Salespeople assigned to ${details.join(', ')} (${result.total_assignments} total assignments)`,
        })
      }

      // Clear selection and reload resellers
      selection.clear()
      refreshAll()
    } catch (error: any) {
      console.error('Error bulk assigning salespeople:', error)
      toast({
        title: "Error",
        description: error.response?.data?.message || error.response?.data?.errors?.[0] || "Failed to assign salespeople",
        variant: "destructive",
      })
    }
  }

  const resetResellerForm = () => {
    setResellerForm(emptyResellerForm())
    setCodeError("")
    setEditingReseller(null)
  }

  const handleCodeChange = (newCode: string) => {
    setResellerForm({...resellerForm, code: newCode})

    if (newCode === "700") {
      setCodeError("Code '700' is reserved and cannot be used")
    } else {
      setCodeError("")
    }
  }

  const startEditReseller = (reseller: Reseller) => {
    setEditingReseller(reseller)
    setResellerForm(resellerFormFrom(reseller))
    setCodeError("")
    setIsResellerDialogOpen(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // The in-browser name-or-code filter and the code-then-name re-sort that used
  // to live here are GONE: both now run in Postgres over the WHOLE set
  // (`Api::V1::ResellersController#index`), because with the list paginated they
  // would otherwise have meant "search and sort within the page you happen to
  // be looking at". The rows the table renders are `pageResellers`, exactly as
  // the server ordered them.

  // Edit/Create reseller form body — shared between the create + edit
  // dialogs (the markup is identical apart from element ids).
  const resellerFormBody = (idPrefix: string) => (
    <Box component="form" id="reseller-form" onSubmit={handleCreateReseller} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Basic Information */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500 }}>Basic Information</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}name`}
            label="Reseller Name *"
            size="small"
            fullWidth
            value={resellerForm.name}
            onChange={(e) => setResellerForm({...resellerForm, name: e.target.value})}
            placeholder="Enter reseller name"
            required
          />
          <Box>
            <TextField
              id={`${idPrefix}reseller-code`}
              label="Reseller Code"
              size="small"
              fullWidth
              value={resellerForm.code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="Enter 3-digit code (e.g., 123)"
              error={!!codeError}
              inputProps={{ maxLength: 3, pattern: "[0-9]{3}", title: "Reseller code must be exactly 3 digits" }}
            />
            {codeError ? (
              <Typography sx={{ fontSize: 14, color: "error.main", mt: 0.5 }}>{codeError}</Typography>
            ) : (
              <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>Optional 3-digit numeric code (700 is reserved)</Typography>
            )}
          </Box>
        </Box>

        {/* Assigned Salespeople — for a holder of crm:resellers_salespeople, right after name and code */}
        {canAssignResellerSalespeople() && (
          <Box>
            <MultiUserSelector
              assignableFallback
              value={resellerForm.assigned_salespeople_ids || []}
              onValueChange={(value) => setResellerForm({ ...resellerForm, assigned_salespeople_ids: value })}
              label="Assigned Salespeople"
              placeholder="Select salespeople..."
            />
          </Box>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}website`}
            label="Website"
            size="small"
            fullWidth
            value={resellerForm.website}
            onChange={(e) => setResellerForm({...resellerForm, website: e.target.value})}
            placeholder="https://example.com"
            type="url"
          />
          {/* Reseller-of-reseller. This is not an org-chart nicety: a parent
              SEES its descendants' customers, so this select edits the shape of
              the CRM scope wall. The reseller being edited and its own
              descendants are excluded from the options — picking one would be a
              cycle, which crm-api rejects, and offering a choice that can only
              422 is worse than not offering it. */}
          <TextField
            id={`${idPrefix}parent_reseller_id`}
            select
            label="Parent reseller"
            size="small"
            fullWidth
            value={resellerForm.parent_reseller_id}
            onChange={(e) => setResellerForm({...resellerForm, parent_reseller_id: e.target.value})}
            helperText="A parent can see everything its sub-resellers can. Leave empty for a top-level reseller."
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ "data-cy": "reseller-parent-select" }}
          >
            <option value="">No parent (top level)</option>
            {parentOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code ? `${r.code} — ${r.name}` : r.name}
              </option>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Billing Identity — spec A §3.2. Kept as its own section, below the
          address, because the two answer DIFFERENT questions: the address is
          where XOP bills this reseller; these fields are the identity a
          reseller-issued invoice is drawn in when the reseller bills its own
          customer. Collapsing them into one block is how a reseller ends up
          issuing documents under XOP's tax id. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500 }}>Billing Identity</Typography>
        <Box sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08), border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.2), borderRadius: 1, p: 1.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ mt: 0.25, flexShrink: 0, color: "primary.main", display: "flex" }}><Info size={16} /></Box>
          <Typography sx={{ fontSize: 14, color: "primary.dark" }}>
            Used on documents this reseller issues in its own name. Leave blank to fall back to the trade name and address above.
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}legal_name`}
            label="Legal name"
            size="small"
            fullWidth
            value={resellerForm.legal_name}
            onChange={(e) => setResellerForm({...resellerForm, legal_name: e.target.value})}
            placeholder="Acme Communications LLC"
            inputProps={{ "data-cy": "reseller-legal-name" }}
          />
          <TextField
            id={`${idPrefix}tax_id`}
            label="Tax ID / VAT number"
            size="small"
            fullWidth
            value={resellerForm.tax_id}
            onChange={(e) => setResellerForm({...resellerForm, tax_id: e.target.value})}
            placeholder="12-3456789"
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}currency`}
            label="Currency *"
            size="small"
            fullWidth
            required
            value={resellerForm.currency}
            onChange={(e) => setResellerForm({...resellerForm, currency: e.target.value.toUpperCase()})}
            placeholder="USD"
            inputProps={{ maxLength: 3 }}
            helperText="ISO code"
          />
          <TextField
            id={`${idPrefix}billing_email`}
            label="Billing email"
            size="small"
            fullWidth
            type="email"
            value={resellerForm.billing_email}
            onChange={(e) => setResellerForm({...resellerForm, billing_email: e.target.value})}
            placeholder="ap@acme.example"
          />
          <TextField
            id={`${idPrefix}billing_phone`}
            label="Billing phone"
            size="small"
            fullWidth
            value={resellerForm.billing_phone}
            onChange={(e) => setResellerForm({...resellerForm, billing_phone: e.target.value})}
            placeholder="+1 555 0100"
          />
        </Box>
        <TextField
          id={`${idPrefix}payment_terms`}
          label="Payment terms"
          size="small"
          fullWidth
          value={resellerForm.payment_terms}
          onChange={(e) => setResellerForm({...resellerForm, payment_terms: e.target.value})}
          placeholder="Net 30"
        />
        <TextField
          id={`${idPrefix}invoice_footer`}
          label="Invoice footer"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={resellerForm.invoice_footer}
          onChange={(e) => setResellerForm({...resellerForm, invoice_footer: e.target.value})}
          placeholder="Thank you for your business."
        />
      </Box>

      {/* Address Information */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500 }}>Address Information</Typography>
        </Box>
        <Box sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08), border: 1, borderColor: (t) => alpha(t.palette.primary.main, 0.2), borderRadius: 1, p: 1.5, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ mt: 0.25, flexShrink: 0, color: "primary.main", display: "flex" }}><Info size={16} /></Box>
          <Typography sx={{ fontSize: 14, color: "primary.dark" }}>
            This address will be automatically used to fill in billing information when generating invoices for this reseller.
          </Typography>
        </Box>
        <TextField
          id={`${idPrefix}address`}
          label="Street Address"
          size="small"
          fullWidth
          value={resellerForm.address}
          onChange={(e) => setResellerForm({...resellerForm, address: e.target.value})}
          placeholder="Enter street address"
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}city`}
            label="City"
            size="small"
            fullWidth
            value={resellerForm.city}
            onChange={(e) => setResellerForm({...resellerForm, city: e.target.value})}
            placeholder="Enter city"
          />
          <TextField
            id={`${idPrefix}state`}
            label="State/Province"
            size="small"
            fullWidth
            value={resellerForm.state}
            onChange={(e) => setResellerForm({...resellerForm, state: e.target.value})}
            placeholder="Enter state or province"
          />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField
            id={`${idPrefix}postal_code`}
            label="Postal Code"
            size="small"
            fullWidth
            value={resellerForm.postal_code}
            onChange={(e) => setResellerForm({...resellerForm, postal_code: e.target.value})}
            placeholder="Enter postal code"
          />
          <TextField
            id={`${idPrefix}country`}
            label="Country"
            size="small"
            fullWidth
            value={resellerForm.country}
            onChange={(e) => setResellerForm({...resellerForm, country: e.target.value})}
            placeholder="Enter country"
          />
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <PageHeader
        title="Reseller Management"
        description="Manage resellers and view their associated customers"
        actions={
          <>
            {/* The PEOPLE inside a company are the Accounts tab of its
                profile (2026-09-12) — no separate System Admin surface. */}
            {/* Import/Export Buttons - Only for admin users */}
            {isAdmin() && (
              <ImportExportButtons
                entityType="resellers"
                entityDisplayName="Resellers"
                apiService={resellersApiService}
                onImportComplete={refreshAll}
                variant="buttons"
                size="default"
              />
            )}

            {/* Add Reseller Button — reseller managers (super_admin or
                sales_manager) can create; the same gate mounts the create/edit dialog. */}
            {canManageResellers() && (
              <>
                <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => { resetResellerForm(); setIsResellerDialogOpen(true) }}>
                  Add Reseller
                </Button>
                <Dialog open={isResellerDialogOpen} onClose={() => setIsResellerDialogOpen(false)} maxWidth="md" fullWidth>
                  <DialogTitle>{editingReseller ? 'Edit Reseller' : 'Create New Reseller'}</DialogTitle>
                  <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                      {editingReseller ? 'Update reseller information and address.' : 'Add a new reseller to your network with complete address information.'}
                    </DialogContentText>
                    {resellerFormBody("")}
                  </DialogContent>
                  <DialogActions>
                    <Button type="button" variant="outlined" color="inherit" onClick={() => setIsResellerDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" form="reseller-form" variant="contained" disabled={!!codeError}>
                      {editingReseller ? 'Update' : 'Create'} Reseller
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </>
        }
      />

      <ResellerManagementTabs
        activeId={activeTab}
        onTabChange={(id) => setTab(id)}
        showApplications={canSeeApplications}
        openApplicationCount={openApplicationCount}
      />

      {activeTab === "applications" ? (
        // The onboarding queue + its review pages, where the person who
        // activates a reseller actually looks for it (was a card above the
        // table, and a System Admin tab, before 2026-09-12).
        <ResellerApplicationsTab />
      ) : (
      <>
      {/* Resellers Table */}
      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, now
            // debounced (500ms) and answered by Postgres rather than by a
            // client-side substring test. The two facets sit beside it.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <TextField
                  size="small"
                  placeholder="Search resellers..."
                  value={search.input}
                  onChange={(e) => search.setInput(e.target.value)}
                  autoComplete="off"
                  sx={{ width: { xs: "100%", sm: 300 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} color="#9ca3af" />
                      </InputAdornment>
                    ),
                    ...(search.isSearching ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Searching...</Typography>
                        </InputAdornment>
                      ),
                    } : {}),
                  }}
                />
              }
            />
          }
        />
        <CardContent>
          {/* Bulk Selection Indicator - Shows above table */}
          {selection.selected.length > 0 && !isLoading && pageResellers.length > 0 && isAdmin() && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                  Selected {selection.selected.length} {selection.selected.length === 1 ? 'item' : 'items'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={selection.clear}
                >
                  Clear Selection
                </Button>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Users size={16} />}
                  onClick={handleBulkAssignSalespeople}
                >
                  Assign Salespeople
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<Trash2 size={16} />}
                  onClick={handleBulkDeleteResellers}
                >
                  Delete Selected
                </Button>
              </Box>
            </Box>
          )}

          {isLoading ? (
            <PageLoader />
          ) : pageResellers.length === 0 ? (
            // Names whichever narrowing actually emptied the list: filtering to
            // nothing used to claim there were no resellers at all.
            <TableEmptyState
              entityNoun="resellers"
              hasSearch={search.input.trim().length > 0}
              activeFilterCount={table.activeFilterCount}
              onClearFilters={table.clearFacets}
              emptyTitle="No resellers found"
              emptyBody="Get started by adding your first reseller to manage your partner network."
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {isAdmin() && (
                      <TableCell sx={{ width: 50 }}>
                        <Checkbox
                          size="small"
                          checked={selection.selected.length === pageResellers.length && pageResellers.length > 0}
                          onChange={handleSelectAllResellers}
                          inputProps={{ "aria-label": "Select all resellers" }}
                        />
                      </TableCell>
                    )}
                    {/* The seven DATA columns, in `RESELLERS_TABLE` order —
                        which is the order the body cells below are written in,
                        so the two must be edited together. The checkbox and
                        actions cells stay plain <TableCell>s: neither maps to a
                        server column, so neither can sort. */}
                    {columns.map((col) => (
                      <SortableHeader
                        key={col.key}
                        column={col.key}
                        label={col.label}
                        sort={table.sort}
                        dir={table.dir}
                        onSort={table.toggleSort}
                        {...(col.align ? { align: col.align } : {})}
                        {...(col.key === "code" ? { sx: { whiteSpace: "nowrap" } } : {})}
                      />
                    ))}
                    <TableCell sx={{ width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageResellers.map((reseller, index) => (
                    <TableRow
                      key={reseller.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleViewDetails(reseller)}
                    >
                      {isAdmin() && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Box onClick={(e) => {
                            e.stopPropagation()
                            handleSelectReseller(reseller.id, index, e.shiftKey)
                          }}>
                            <Checkbox
                              size="small"
                              checked={selectedIds.has(reseller.id)}
                              inputProps={{ "aria-label": `Select ${reseller.name}` }}
                            />
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Box sx={{ width: 40, height: 40, bgcolor: (t) => alpha(t.palette.primary.main, 0.1), borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Building2 size={20} color="#9333ea" />
                          </Box>
                          <Typography sx={{ fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
                            {reseller.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {reseller.code ? (
                          <Box component="span" sx={{ display: "inline-block", whiteSpace: "nowrap", fontSize: 14, fontWeight: 600, color: "secondary.main", bgcolor: (t) => alpha(t.palette.secondary.main, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                            {reseller.code}
                          </Box>
                        ) : (
                          <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {reseller.assigned_salespeople && reseller.assigned_salespeople.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {reseller.assigned_salespeople.slice(0, 2).map((salesperson) => (
                              <Chip key={salesperson.id} size="small" label={salesperson.full_name} sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: "primary.dark" }} />
                            ))}
                            {reseller.assigned_salespeople.length > 2 && (
                              <Chip size="small" label={`+${reseller.assigned_salespeople.length - 2} more`} sx={{ bgcolor: "action.hover", color: "text.secondary" }} />
                            )}
                          </Box>
                        ) : (
                          <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>
                      {/* align right, to sit under the right-aligned Dealers
                          header `RESELLERS_TABLE` declares. `??`, never `||`:
                          `||` treats a genuine ZERO as falsy. This is the
                          number the server's `dealers` sort orders on. */}
                      <TableCell align="right">
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                          {reseller.dealer_count ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(reseller.city?.trim() || reseller.state?.trim() || reseller.country?.trim()) ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 14, color: "text.secondary" }}>
                            <Box sx={{ flexShrink: 0, display: "flex", color: "text.disabled" }}><MapPin size={16} /></Box>
                            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>
                              {[reseller.city, reseller.state, reseller.country].filter(v => v && v.trim()).join(', ')}
                            </Box>
                          </Box>
                        ) : (
                          <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {reseller.created_by ? (
                          <Typography component="span" sx={{ fontSize: 14, color: "text.primary" }}>
                            {reseller.created_by.full_name}
                          </Typography>
                        ) : (
                          <Typography component="span" sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                          {formatDate(reseller.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          sx={{ width: 32, height: 32 }}
                          aria-label="Open menu"
                          onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, id: reseller.id }) }}
                        >
                          <MoreHorizontal size={16} />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchor?.id === reseller.id ? menuAnchor.el : null}
                          open={menuAnchor?.id === reseller.id}
                          onClose={() => setMenuAnchor(null)}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                                                  // The Menu portals into <body>, but React events bubble
                          // through the REACT tree, so every click inside it — an
                          // item, the padding between items, the click-away
                          // backdrop — also reached the TableRow's onClick and
                          // opened the reseller profile. The IconButton stops it
                          // for the trigger only; the popup needs its own.
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                          <Divider />
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuAnchor(null)
                              handleViewDetails(reseller)
                            }}
                          >
                            <Box sx={{ mr: 1, display: "flex" }}><Eye size={16} /></Box>
                            View Details
                          </MenuItem>
                          {canManageResellers() && (
                            <>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuAnchor(null)
                                  startEditReseller(reseller)
                                }}
                              >
                                <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                                Edit Reseller
                              </MenuItem>
                              <Divider />
                              <MenuItem
                                sx={{ color: "error.main" }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuAnchor(null)
                                  setPendingDeleteReseller(reseller)
                                }}
                              >
                                <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                                Delete Reseller
                              </MenuItem>
                            </>
                          )}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination — new here: the list used to render EVERY reseller the
              actor could see in one table. Hidden on a single page, so a small
              partner network looks exactly as it did. */}
          {Math.ceil(totalCount / PER_PAGE) > 1 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 2 }}>
              <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                Showing {((table.page - 1) * PER_PAGE) + 1} to{' '}
                {Math.min(table.page * PER_PAGE, totalCount)} of {totalCount} resellers
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => table.setPage(table.page - 1)}
                  disabled={table.page <= 1}
                >
                  Previous
                </Button>
                <Box component="span" sx={{ fontSize: 14 }}>
                  Page {table.page} of {Math.ceil(totalCount / PER_PAGE)}
                </Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => table.setPage(table.page + 1)}
                  disabled={table.page >= Math.ceil(totalCount / PER_PAGE)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Bulk Delete Modal */}
      <BulkDeleteResellerModal
        isOpen={isBulkDeleteDialogOpen}
        onClose={() => setIsBulkDeleteDialogOpen(false)}
        selectedResellers={pageResellers.filter(r => selectedIds.has(r.id))}
        onConfirmDelete={confirmBulkDeleteResellers}
      />

      {/* Bulk Assign Salespeople Modal */}
      <BulkAssignSalespeopleModal
        isOpen={isBulkAssignDialogOpen}
        onClose={() => setIsBulkAssignDialogOpen(false)}
        selectedResellers={pageResellers.filter(r => selectedIds.has(r.id))}
        onConfirmAssign={confirmBulkAssignSalespeople}
      />

      {/* Single-reseller delete confirmation */}
      <ConfirmDialog
        isOpen={pendingDeleteReseller !== null}
        onClose={() => setPendingDeleteReseller(null)}
        title="Delete reseller?"
        description={
          <>
            <strong>{pendingDeleteReseller?.name ?? ""}</strong> will be deleted permanently and
            the reseller association will be removed from every customer and contact that
            referenced it. This cannot be undone. A reseller that has <strong>issued invoices or
            quotes</strong> cannot be deleted — an issued document has to keep the reseller it
            was issued by. Documents you have already deleted do not count.
          </>
        }
        confirmLabel="Delete reseller"
        onConfirm={confirmDeleteReseller}
      />
      </>
      )}
    </Box>
  )
}

export default function ResellersPage() {
  return (
    <PagePermissionGuard
      requiredPermission="resellers:access"
      redirectTo="/crm"
    >
      <ResellersContent />
    </PagePermissionGuard>
  )
}
